import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

const SECTION_CONFIG = {
  left: {
    color: '#E74C3C',
    offsetMeters: -1.75, // negative = left side of travel direction
    strokeWeight: 3,
    strokeOpacity: 1,
    label: 'Left Line',
  },
  right: {
    color: '#27AE60',
    offsetMeters: 1.75, // positive = right side of travel direction
    strokeWeight: 3,
    strokeOpacity: 1,
    label: 'Right Line',
  },
  aux: {
    color: '#B366FF',
    offsetMeters: 3.5, // further right — edge line / adjacent boundary
    strokeWeight: 2,
    strokeOpacity: 0.85,
    label: 'Aux Line',
  },
};

const EARTH_RADIUS_M = 6378137;
const FLIP_PERP = false;

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

const getSegmentPerp = (p1, p2) => {
  const lat1 = toRad(p1.lat);
  const cosLat = Math.cos(lat1);

  const dx = (p2.lng - p1.lng) * cosLat;
  const dy = p2.lat - p1.lat;

  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-10) return { px: 0, py: 0 };

  if (FLIP_PERP) {
    return {
      px: dy / len / cosLat,
      py: -dx / len,
    };
  }

  return {
    px: -dy / len / cosLat,
    py: dx / len,
  };
};

const offsetPoint = (point, px, py, meters) => {
  const lat1 = toRad(point.lat);
  const dLat = (py * meters) / EARTH_RADIUS_M;
  const dLng = (px * meters) / (EARTH_RADIUS_M * Math.cos(lat1));
  return {
    lat: point.lat + toDeg(dLat),
    lng: point.lng + toDeg(dLng),
  };
};

const getMiterOffsetPath = (path, offsetMeters) => {
  if (!window.google || path.length < 2) return path;

  const MITER_LIMIT = 3;
  const result = [];

  for (let i = 0; i < path.length; i++) {
    if (i === 0) {
      const perp = getSegmentPerp(path[0], path[1]);
      result.push(offsetPoint(path[0], perp.px, perp.py, offsetMeters));
    } else if (i === path.length - 1) {
      const perp = getSegmentPerp(path[i - 1], path[i]);
      result.push(offsetPoint(path[i], perp.px, perp.py, offsetMeters));
    } else {
      const perp1 = getSegmentPerp(path[i - 1], path[i]);
      const perp2 = getSegmentPerp(path[i], path[i + 1]);

      const avgPx = (perp1.px + perp2.px) / 2;
      const avgPy = (perp1.py + perp2.py) / 2;
      const avgLen = Math.sqrt(avgPx * avgPx + avgPy * avgPy);

      const scale = avgLen > 1e-10 ? Math.min(1 / avgLen, MITER_LIMIT) : 1;

      result.push(offsetPoint(path[i], avgPx * scale, avgPy * scale, offsetMeters));
    }
  }

  return result;
};

const extractPath = (lineData) => {
  const path = [];

  if (lineData?.startGpsPoint && lineData?.startTime) {
    path.push({
      lat: lineData.startGpsPoint.latitude,
      lng: lineData.startGpsPoint.longitude,
      timeString: lineData.startTime,
      time: new Date(lineData.startTime).getTime() - 1,
    });
  }

  if (Array.isArray(lineData?.gpsPoints)) {
    lineData.gpsPoints.forEach((p) =>
      path.push({
        lat: p.latitude,
        lng: p.longitude,
        timeString: p.timeString,
        time: new Date(p.timeString).getTime(),
      })
    );
  }

  if (lineData?.endGpsPoint && lineData?.endTime) {
    path.push({
      lat: lineData.endGpsPoint.latitude,
      lng: lineData.endGpsPoint.longitude,
      timeString: lineData.endTime,
      time: new Date(lineData.endTime).getTime() + 1,
    });
  }

  path.sort((a, b) => a.time - b.time);

  const deduped = [];
  for (let i = 0; i < path.length; i++) {
    const prev = deduped[deduped.length - 1];
    const curr = path[i];

    if (!prev || prev.lat !== curr.lat || prev.lng !== curr.lng) {
      deduped.push(curr);
    }
  }

  return deduped;
};
const LINE_SPACING_METERS = 2.0;

const parseLaneDescriptors = (laneGpsData) => {
  if (!laneGpsData) return [];

  const lanesArray = Array.isArray(laneGpsData) ? laneGpsData : [laneGpsData];
  const descriptors = [];

  lanesArray.forEach((lane, laneIndex) => {
    const laneId = lane.laneid ?? `lane_${laneIndex}`;
    const sections = lane.lines;
    if (!sections) return;

    Object.entries(sections).forEach(([sectionKey, sectionLines]) => {
      const config = SECTION_CONFIG[sectionKey];
      if (!config) return;

      const linesWithPaths = (sectionLines || [])
        .map((lineData) => ({ lineData, rawPath: extractPath(lineData) }))
        .filter(({ rawPath }) => rawPath.length >= 2)
        .sort((a, b) => (a.lineData.lineId ?? 0) - (b.lineData.lineId ?? 0));

      linesWithPaths.forEach(({ lineData, rawPath }, indexInSection) => {
        const lineType = lineData?.type || 'Solid White';
        const lineKey = `line_${lineData.lineId}`;

        const direction = config.offsetMeters < 0 ? -1 : 1;
        const offsetMeters = config.offsetMeters + indexInSection * LINE_SPACING_METERS * direction;

        descriptors.push({
          key: `${laneId}_${sectionKey}_${lineKey}`,
          laneId,
          sectionKey,
          lineKey,
          rawPath,
          config: { ...config, offsetMeters },
          lineType,
        });
      });
    });
  });

  return descriptors;
};

const getOverallStartEndPoints = (descriptors) => {
  let startPoint = null;
  let endPoint = null;

  descriptors.forEach((descriptor) => {
    descriptor.rawPath.forEach((point) => {
      if (!Number.isFinite(point.time)) return;
      if (!startPoint || point.time < startPoint.time) startPoint = point;
      if (!endPoint || point.time > endPoint.time) endPoint = point;
    });
  });

  return { startPoint, endPoint };
};

let googleMapsPromise = null;

const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve();

  if (!googleMapsPromise) {
    googleMapsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=marker`;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        googleMapsPromise = null;
        reject(new Error('Failed to load Google Maps'));
      };
      document.head.appendChild(script);
    });
  }

  return googleMapsPromise;
};

/**
 * Generates custom native Advanced Polyline styles based on lane types.
 * Standardizes complex lines such as dashed profiles and compound double paths.
 */
const getLaneStyleOptions = (lineType, isVisible) => {
  const defaultOpacity = isVisible ? 1 : 0;

  // Configuration mapping fallback colors based on real-world assets
  const WHITE = '#FFFFFF';
  const YELLOW = '#FFD700';
  const ROAD_BG = '#1A1A1A'; // Cuts through double lines to create a perfect gap

  switch (lineType) {
    case 'Dashed White': // Dashed White
      return {
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-2 L 0,2',
              strokeColor: WHITE,
              strokeOpacity: defaultOpacity,
              strokeWeight: 5,
              scale: 2,
            },
            offset: '0',
            repeat: '24px',
          },
        ],
      };

    case 'Double Yellow': // Double Yellow
      return {
        strokeColor: YELLOW,
        strokeOpacity: defaultOpacity,
        strokeWeight: 6, // Thick underlying baseline
        icons: [
          {
            // Middle split overlay masking track
            icon: {
              path: 'M 0,-1 L 0,1',
              strokeColor: ROAD_BG,
              strokeOpacity: defaultOpacity,
              strokeWeight: 2,
              scale: 4,
            },
            offset: '0',
            repeat: '8px',
          },
        ],
      };

    case 'Solid White': // Solid White
      return {
        strokeColor: WHITE,
        strokeOpacity: defaultOpacity,
        strokeWeight: 3,
        icons: null,
      };

    case 'Solid Yellow': // Solid Yellow
      return {
        strokeColor: YELLOW,
        strokeOpacity: defaultOpacity,
        strokeWeight: 3,
        icons: null,
      };

    case 'Dashed Yellow': // Dashed Yellow
      return {
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-2 L 0,2',
              strokeColor: YELLOW,
              strokeOpacity: defaultOpacity,
              strokeWeight: 3,
              scale: 2,
            },
            offset: '0',
            repeat: '24px',
          },
        ],
      };

    case 'Double White': // Double White
      return {
        strokeColor: WHITE,
        strokeOpacity: defaultOpacity,
        strokeWeight: 6,
        icons: [
          {
            icon: {
              path: 'M 0,-1 L 0,1',
              strokeColor: ROAD_BG,
              strokeOpacity: defaultOpacity,
              strokeWeight: 2,
              scale: 4,
            },
            offset: '0',
            repeat: '8px',
          },
        ],
      };

    default:
      return {
        strokeColor: WHITE,
        strokeOpacity: defaultOpacity,
        strokeWeight: 3,
        icons: null,
      };
  }
};

const createPolyline = (descriptor, isVisible, map) => {
  const { rawPath, config, lineType } = descriptor;
  const pathForPolyline = rawPath.map(({ lat, lng }) => ({ lat, lng }));
  const offsetPath = getMiterOffsetPath(pathForPolyline, config.offsetMeters);

  // Merge spatial structure offset controls with semantic line markings rules
  const laneStyles = getLaneStyleOptions(lineType, isVisible);

  return new window.google.maps.Polyline({
    path: offsetPath,
    map,
    clickable: true,
    zIndex: 2,
    ...laneStyles,
  });
};

const getClosestGpsPointTime = (rawPath, clickLatLng) => {
  if (!rawPath || rawPath.length === 0) return null;

  const clickLat = clickLatLng.lat;
  const clickLng = clickLatLng.lng;
  let bestPoint = rawPath[0];
  let bestDistance = Infinity;

  rawPath.forEach((point) => {
    const latDiff = point.lat - clickLat;
    const lngDiff = (point.lng - clickLng) * Math.cos(toRad(clickLat));
    const distanceSquared = latDiff * latDiff + lngDiff * lngDiff;
    if (distanceSquared < bestDistance) {
      bestDistance = distanceSquared;
      bestPoint = point;
    }
  });

  return bestPoint.timeString || (bestPoint.time ? new Date(bestPoint.time).toISOString() : null);
};

// Same pin style as DriveMapComponent — FontAwesome glyph inside a colored PinElement.
const createPin = (iconClass, bgColor, borderColor) => {
  const iconDiv = document.createElement('div');
  iconDiv.innerHTML = `<i class="${iconClass}"></i>`;
  iconDiv.style.fontSize = '22px';

  return new window.google.maps.marker.PinElement({
    glyph: iconDiv,
    glyphColor: '#FFF',
    background: bgColor,
    borderColor: borderColor,
    scale: 1.3,
  });
};

const LaneGpsMapComponent = ({
  laneGpsData,
  visibleLanes: externalVisibleLanes,
  videoStartTime,
  videoTime,
  videoPlaying,
  centerLat = 39.8646,
  centerLng = -75.7417,
  initialZoom = 13,
  apiKey = 'AIzaSyBrYTn6FUEGr_84F1UPtsYa7l_sp4bWWMo',
  onGpsPointClick,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polylinesRef = useRef({});
  const descriptorsRef = useRef([]);
  const markerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(null);

  const onGpsPointClickRef = useRef(onGpsPointClick);
  useEffect(() => {
    onGpsPointClickRef.current = onGpsPointClick;
  }, [onGpsPointClick]);

  const getVisibility = useCallback(
    (key) => {
      if (externalVisibleLanes && key in externalVisibleLanes) {
        return externalVisibleLanes[key];
      }
      return true;
    },
    [externalVisibleLanes]
  );

  const visibilitySignature = useMemo(() => {
    if (!externalVisibleLanes) return '';
    try {
      return JSON.stringify(externalVisibleLanes);
    } catch {
      return '';
    }
  }, [externalVisibleLanes]);

  useEffect(() => {
    loadGoogleMaps(apiKey)
      .then(() => setMapLoaded(true))
      .catch((err) => setMapError(err.message));
  }, [apiKey]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: centerLat, lng: centerLng },
      zoom: initialZoom,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: true,
      mapId: '6317ec852b61f66ba5221f1e',
    });
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapInstanceRef.current || !laneGpsData) return;

    Object.values(polylinesRef.current).forEach((pl) => pl.setMap(null));
    polylinesRef.current = {};

    const descriptors = parseLaneDescriptors(laneGpsData);
    descriptorsRef.current = descriptors;

    descriptors.forEach((descriptor) => {
      const { key, rawPath } = descriptor;
      const visible = getVisibility(key);
      const polyline = createPolyline(descriptor, visible, mapInstanceRef.current);

      polyline.addListener('click', (event) => {
        if (!onGpsPointClickRef.current || rawPath.length === 0 || !event?.latLng) return;

        const clickedLatLng = event.latLng.toJSON();
        const timeString = getClosestGpsPointTime(rawPath, clickedLatLng);
        if (timeString) {
          onGpsPointClickRef.current(timeString);
        }
      });

      polylinesRef.current[key] = polyline;
    });

    const visibleKeys = Object.keys(polylinesRef.current).filter((key) => getVisibility(key));
    fitMapToBounds(visibleKeys);

    const { startPoint, endPoint } = getOverallStartEndPoints(descriptors);

    if (startMarkerRef.current) {
      startMarkerRef.current.map = null;
      startMarkerRef.current = null;
    }
    if (endMarkerRef.current) {
      endMarkerRef.current.map = null;
      endMarkerRef.current = null;
    }

    if (startPoint) {
      const startPin = createPin('fa fa-circle-play', '#24BD05', '#24BD05');
      startMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: startPoint.lat, lng: startPoint.lng },
        map: mapInstanceRef.current,
        title: 'Start',
        content: startPin.element,
      });
    }

    if (endPoint) {
      const endPin = createPin('fa fa-circle-stop', '#DC1709', '#DC1709');
      endMarkerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: endPoint.lat, lng: endPoint.lng },
        map: mapInstanceRef.current,
        title: 'End',
        content: endPin.element,
      });
    }
  }, [laneGpsData, mapLoaded]);

  useEffect(() => {
    const visibleKeys = [];

    Object.entries(polylinesRef.current).forEach(([key, polyline]) => {
      const visible = getVisibility(key);
      const descriptor = descriptorsRef.current.find((d) => d.key === key);

      if (descriptor) {
        const laneStyles = getLaneStyleOptions(descriptor.lineType, visible);
        polyline.setOptions(laneStyles);
      }

      if (visible) visibleKeys.push(key);
    });

    if (visibleKeys.length > 0) {
      fitMapToBounds(visibleKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilitySignature]);

  // --- Live GPS marker while video plays ---
  useEffect(() => {
    if (!mapInstanceRef.current || !videoPlaying || videoTime == null || videoStartTime == null) {
      return;
    }

    const points = descriptorsRef.current
      .filter((descriptor) => getVisibility(descriptor.key))
      .flatMap((descriptor) => descriptor.rawPath)
      .filter((point) => Number.isFinite(point.time));

    if (points.length === 0) return;

    const targetTime = videoStartTime + videoTime * 1000;
    const closestPoint = points.reduce((closest, point) => {
      const pointDistance = Math.abs(point.time - targetTime);
      const closestDistance = Math.abs(closest.time - targetTime);
      return pointDistance < closestDistance ? point : closest;
    });
    const position = { lat: closestPoint.lat, lng: closestPoint.lng };

    if (!markerRef.current) {
      const dot = document.createElement('div');
      dot.style.width = '16px';
      dot.style.height = '16px';
      dot.style.borderRadius = '50%';
      dot.style.backgroundColor = '#1976D2';
      dot.style.border = '2px solid #FFFFFF';
      dot.style.boxShadow = '0 0 2px rgba(0,0,0,0.5)';

      markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position,
        title: 'Current GPS position',
        content: dot,
        zIndex: 10,
      });
    } else {
      markerRef.current.position = position;
      markerRef.current.map = mapInstanceRef.current;
    }
  }, [videoStartTime, videoTime, videoPlaying, getVisibility]);

  useEffect(
    () => () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (startMarkerRef.current) {
        startMarkerRef.current.map = null;
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.map = null;
        endMarkerRef.current = null;
      }
    },
    []
  );

  const fitMapToBounds = (keys) => {
    if (!mapInstanceRef.current || keys.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    keys.forEach((key) => {
      const polyline = polylinesRef.current[key];
      if (!polyline) return;
      const path = polyline.getPath();
      for (let i = 0; i < path.getLength(); i++) {
        bounds.extend(path.getAt(i));
        hasPoints = true;
      }
    });

    if (hasPoints) {
      mapInstanceRef.current.fitBounds(bounds, 60);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {!mapLoaded && !mapError && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#12121a',
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2, fontSize: 13 }}>
            LOADING MAP…
          </Typography>
        </Box>
      )}

      {mapError && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#12121a',
          }}
        >
          <Typography sx={{ color: '#FF6B6B', fontSize: 13 }}>{mapError}</Typography>
        </Box>
      )}

      <Box ref={mapRef} sx={{ width: '100%', height: '100%', minHeight: '400px' }} />
    </Box>
  );
};

LaneGpsMapComponent.propTypes = {
  laneGpsData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  visibleLanes: PropTypes.object,
  videoStartTime: PropTypes.number,
  videoTime: PropTypes.number,
  videoPlaying: PropTypes.bool,
  centerLat: PropTypes.number,
  centerLng: PropTypes.number,
  initialZoom: PropTypes.number,
  apiKey: PropTypes.string,
  onGpsPointClick: PropTypes.func,
};

export default LaneGpsMapComponent;
